<template>
  <v-container id="wizard" fluid tag="section">
    <validation-observer v-slot="{ valid }">
      <base-login-material-wizard
        ref="baseLoginMat"
        v-model="tab"
        :available-steps="availableSteps"
        :items="tabs"
        :disabledBtnLogar="disabledBtnLogar"
        :loading="loading"
        :showSamlLoginButton="showSamlLoginButton"
        class="mx-auto"
        :actionLink="{
          text: 'Esqueceu sua senha?',
          to: '/esqueceu-senha',
          showIf: tab === 0
        }"
        @click:next="next(valid)"
        @click:redirectToSamlLogin="redirectToSamlLogin"
      >
        <!-- Tab 0: Credenciais -->
        <v-tab-item class="pb-1">
          <form @submit.prevent="next(valid)">
            <v-row class="mx-auto" justify="space-around">
              <v-col cols="12" md="11">
                <validation-provider v-slot="{ errors }" rules="required" name="login">
                  <v-text-field
                    ref="loginField"
                    v-model="login"
                    :error-messages="errors"
                    color="primary"
                    label="Login*"
                    prepend-icon="mdi-account-outline"
                    autocomplete="username"
                    validate-on-blur
                  />
                </validation-provider>

                <validation-provider v-slot="{ errors }" rules="required" name="password">
                  <v-text-field
                    ref="passwordField"
                    v-model="password"
                    :error-messages="errors"
                    :type="mostrarSenha ? 'text' : 'password'"
                    color="primary"
                    label="Senha*"
                    prepend-icon="mdi-lock-outline"
                    :append-icon="mostrarSenha ? 'mdi-eye' : 'mdi-eye-off'"
                    autocomplete="current-password"
                    validate-on-blur
                    @keyup.enter="next(valid)"
                    @click:append="mostrarSenha = !mostrarSenha"
                  />
                </validation-provider>
              </v-col>
            </v-row>
          </form>
        </v-tab-item>

        <!-- Tab 1: Seleção de empresa -->
        <v-tab-item class="pb-1">
          <form @submit.prevent="next(valid)">
            <v-row v-if="selectedCompany.logo" class="mt-n5">
              <v-col cols="12" md="12" justify="center" align="center">
                <v-img :src="selectedCompany.logo" width="180" />
              </v-col>
            </v-row>
            <v-row no-gutters class="mb-0 pb-0">
              <v-col cols="12" md="12">
                <v-responsive max-width="500">
                  <div class="display-1 font-weight-medium mb-6 text-center">
                    <span v-if="!selectedCompany.id" class="grey--text text--darken-2">Selecione a empresa</span>
                  </div>
                  <select-company ref="refSelectEmp" :type="'outlined'" @companySelected="companySelected" />
                </v-responsive>
              </v-col>
            </v-row>
          </form>
        </v-tab-item>
      </base-login-material-wizard>
    </validation-observer>
  </v-container>
</template>

<script>
  import routes from '@/store/modules/routes';
  import BaseLoginMaterialWizard from '@/components/login/base/BaseLoginMaterialWizard.vue';
  import SelectCompany from '@/components/general/SelectCompany.vue';
  import SamlAuthService from '@/services/saml/SamlAuthService.js';

  export default {
    name: 'LoginWizard',
    components: {
      BaseLoginMaterialWizard,
      SelectCompany
    },
    data() {
      return {
        tab: 0,
        tabs: ['Login', 'Empresa'],
        login: '',
        password: '',
        loading: false,
        mostrarSenha: false,
        authenticated: false,
        isSamlLogin: false,
        samlToken: null,
        showSamlLoginButton: false,
        selectedCompany: {
          id: null,
          name: null,
          logo: null
        }
      };
    },
    computed: {
      availableSteps() {
        return [0, 1];
      },
      disabledBtnLogar() {
        if (this.tab === 0) return !(this.login && this.password);
        if (this.tab === 1) return !this.selectedCompany.id;
        return true;
      }
    },
    created() {
      const hashParams = new URLSearchParams(window.location.hash.split('?')[1]);

      const jwtRecoveryKey = this.$route.query.jwtRecoveryKey || hashParams.get('jwtRecoveryKey');
      const samlError = hashParams.get('error');

      if (jwtRecoveryKey) {
        this.jwtRecoveryKey(jwtRecoveryKey);
      }

      if (samlError) {
        this.$toast.error(samlError, '', {
          position: 'topRight'
        });
      }

      this.loadSamlStatus();
    },
    mounted() {
      this.$nextTick(() => {
        this.checkAutofill();
      });
    },
    methods: {
      companySelected(event) {
        this.selectedCompany = event;
        this.$store.dispatch('setSelectedCompany', this.selectedCompany);
      },
      redirectToSamlLogin() {
        if (!this.showSamlLoginButton) {
          return;
        }

        // URL absoluta para garantir que não seja interpretada pelo Vue Router
        const samlRedirect = process.env.VUE_APP_SAML_SP_REDIRECT_URL;
        window.location.href = samlRedirect;
      },
      async next(valid) {
        if (this.tab === 0) {
          // O observer cobre as duas abas. Só as credenciais da aba 0 dependem dele.
          if (!valid) return;
          await this.authenticate();
        } else if (this.tab === 1) {
          // No login SAML os campos de login/senha ficam vazios e mantêm o observer
          // inválido, então a aba 1 não pode depender de `valid`. A pré-condição aqui
          // é a empresa selecionada, validada por `disabledBtnLogar` e `loginWithCompany`.
          await this.loginWithCompany();
        }
      },
      async authenticate() {
        if ((!this.login || !this.password) && !this.isSamlLogin) return;

        this.loading = true;
        try {
          if (this.isSamlLogin) {
            await this.$store.dispatch('samlAuthenticate', this.token);
          } else {
            await this.$store.dispatch('authenticate', {
              login: this.login,
              password: this.password
            });
          }

          this.authenticated = true;
          this.$refs.baseLoginMat.activateSecondTab();
        } catch (e) {
          this.$toast.error('Login ou senha inválidos.', '', { position: 'topRight' });
        } finally {
          this.loading = false;
        }
      },
      async loginWithCompany() {
        if (!this.selectedCompany.id) return;

        this.loading = true;
        try {
          await this.$store.dispatch('login', this.selectedCompany.id);

          this.$router.push({ name: routes.state.appRoutes['HOME'] });
        } catch (e) {
          this.$toast.error('Erro ao selecionar empresa.', '', { position: 'topRight' });
        } finally {
          this.loading = false;
        }
      },
      checkAutofill() {
        const interval = setInterval(() => {
          const inputs = document.querySelectorAll('input:-webkit-autofill');
          if (inputs.length > 0) {
            inputs.forEach((input) => {
              if (input.autocomplete === 'username') {
                this.$refs.loginField.isFocused = true;
              } else if (input.autocomplete === 'current-password') {
                this.$refs.passwordField.isFocused = true;
              }
            });
            this.$forceUpdate();
            clearInterval(interval);
          }
        }, 100);

        setTimeout(() => clearInterval(interval), 3000);
      },
      getHashParam(name) {
        return new URLSearchParams(window.location.hash.split('?')[1]).get(name);
      },
      async jwtRecoveryKey(key) {
        try {
          const response = await SamlAuthService.jwtRecoveryKey(key);
          const { token } = response.data;
          this.token = token;

          if (!this.token) {
            this.$toast.error('Token inválido ou expirado.', '', {
              position: 'topRight'
            });
            return;
          }

          this.isSamlLogin = true;

          this.authenticate();
        } catch (error) {
          this.$toast.error(`Erro ao autenticar com a Microsoft.`, '', {
            position: 'topRight'
          });
        }
      },
      async loadSamlStatus() {
        try {
          const response = await SamlAuthService.status();
          this.showSamlLoginButton = Boolean(response.data && response.data.enabled);
        } catch (error) {
          this.showSamlLoginButton = false;
        }
      }
    }
  };
</script>
<style lang="sass">
  .v-card.v-card.v-card--account
    border-color: currentColor
    border-width: 4px

    .v-icon
      color: inherit

  .v-card--account,
  .v-card--account:before
    border-radius: 50%
</style>
